import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';
import { cx } from './cx';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
/** Heights from the UI-kit: 32 (rows), 40 (admin), 42 (modals), 48 (auth), 52 (student forms). */
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconAfter?: ReactNode;
  block?: boolean;
  loading?: boolean;
}

export interface ButtonProps extends CommonProps, ButtonHTMLAttributes<HTMLButtonElement> {}

function classes({ variant = 'secondary', size = 'md', block, icon, iconAfter }: CommonProps, className?: string): string {
  return cx(
    styles.button,
    styles[variant],
    styles[size],
    icon ? styles.withIcon : undefined,
    iconAfter ? styles.withIconAfter : undefined,
    block ? styles.block : undefined,
    className,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, icon, iconAfter, block, loading, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={classes({ variant, size, block, icon, iconAfter }, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className={styles.spinner} aria-hidden /> : icon}
      {children}
      {iconAfter}
    </button>
  );
});

export interface ButtonLinkProps extends CommonProps, LinkProps {}

export function ButtonLink({ variant, size, icon, iconAfter, block, className, children, ...rest }: ButtonLinkProps) {
  return (
    <Link className={classes({ variant, size, block, icon, iconAfter }, className)} {...rest}>
      {icon}
      {children}
      {iconAfter}
    </Link>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  size?: 28 | 32 | 40 | 44;
  bordered?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, size = 32, bordered, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cx(styles.iconButton, bordered ? styles.iconBordered : undefined, className)}
      style={{ width: size, height: size }}
      {...rest}
    >
      {children}
    </button>
  );
});
